import {
  Role,
  useEvaluationsLazyQuery,
  ClassificationType,
  ErrorSlice,
} from "@/graphql/types/models";
import { cn } from "@/lib/utils";
import { ERROR_LABELS, ERROR_COLORS } from "@/lib/scoring";
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  FileText,
  FilterIcon,
} from "@credcore/core-ui/icons";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/shadcn-components/Dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn-components/Popover";
import { useOrg } from "@/contexts/OrgContext";
import { EVALUATIONS_PAGE_SIZE } from "@/lib/constants";

interface ErrorBreakdownMiniProps {
  errors: ErrorSlice[];
  personId?: string;
  role?: "ANALYST" | "REVIEWER";
  dateRange?: { from?: Date; to?: Date };
}

export function ErrorBreakdownMini({
  errors,
  personId,
  role = "ANALYST",
  dateRange,
}: ErrorBreakdownMiniProps) {
  const { orgId } = useOrg();
  const [showDetails, setShowDetails] = useState(false);
  const [selectedErrorType, setSelectedErrorType] = useState<
    ClassificationType | "ALL"
  >("ALL");
  const [errorTypePopoverOpen, setErrorTypePopoverOpen] = useState(false);

  const [fetchEvaluations, { data: evalData, loading: evalLoading }] =
    useEvaluationsLazyQuery();

  type EvaluationNode = NonNullable<
    typeof evalData
  >["evaluations"]["edges"][number]["node"];
  const [selectedEvaluation, setSelectedEvaluation] =
    useState<EvaluationNode | null>(null);

  const totalCount = errors.reduce((sum, e) => sum + e.total, 0);

  const errorClassifications = [
    ClassificationType.NUMERIC_ERROR,
    ClassificationType.FORMATTING_ERROR,
    ClassificationType.MISSING_ERROR,
    ClassificationType.PARTIAL_EXTRACTION,
    ClassificationType.INCORRECT_EXTRACTION,
  ];

  const handleShowDetails = () => {
    if (!showDetails && personId) {
      fetchEvaluations({
        variables: {
          filter: {
            orgId,
            role: role === "ANALYST" ? Role.ANALYST : Role.REVIEWER,
            personId,
            dateRange: dateRange
              ? {
                  from: dateRange.from?.toISOString(),
                  to: dateRange.to?.toISOString(),
                }
              : undefined,
            classifications: errorClassifications,
          },
          first: EVALUATIONS_PAGE_SIZE,
        },
      });
    }
    setShowDetails(!showDetails);
  };

  if (errors.length === 0) {
    return <div className="text-sm text-gray-500">No data</div>;
  }

  const allEvaluations = evalData?.evaluations?.edges?.map((e) => e.node) || [];
  const evaluations = allEvaluations.filter((e) => {
    if (selectedErrorType !== "ALL" && e.classification !== selectedErrorType) {
      return false;
    }
    return true;
  });

  const availableErrorTypes = Array.from(
    new Set(allEvaluations.map((e) => e.classification).filter(Boolean)),
  ) as ClassificationType[];

  const renderEvaluationModal = () => {
    if (!selectedEvaluation) return null;

    const classificationType =
      selectedEvaluation.classification ||
      ClassificationType.INCORRECT_EXTRACTION;
    const errorColors =
      ERROR_COLORS[classificationType] ||
      ERROR_COLORS[ClassificationType.INCORRECT_EXTRACTION];
    const errorLabel = ERROR_LABELS[classificationType] || classificationType;

    return (
      <Dialog
        open={!!selectedEvaluation}
        onOpenChange={() => setSelectedEvaluation(null)}
      >
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedEvaluation.document.name}</DialogTitle>
            <p className="text-sm text-gray-500">
              {selectedEvaluation.field.displayName ||
                selectedEvaluation.field.name}
            </p>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "rounded px-2 py-1 text-xs",
                  errorColors.bg,
                  errorColors.text,
                )}
              >
                {errorLabel}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500">
                  Submitted Value
                </p>
                <p className="max-h-32 overflow-y-auto break-words text-sm text-red-600">
                  {selectedEvaluation.value || "-"}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500">
                  Correct Value
                </p>
                <p className="max-h-32 overflow-y-auto break-words text-sm text-green-600">
                  {selectedEvaluation.finalValue || "-"}
                </p>
              </div>
            </div>

            {selectedEvaluation.classificationReason && (
              <div>
                <p className="mb-1 text-xs font-medium text-gray-500">Reason</p>
                <p className="max-h-24 overflow-y-auto text-sm italic text-gray-600">
                  {selectedEvaluation.classificationReason}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      {renderEvaluationModal()}
      <div className="space-y-3">
        <div className="flex h-4 w-full overflow-hidden rounded-full bg-gray-100">
          {errors.map((error) => {
            const width = (error.total / totalCount) * 100;
            const colors =
              ERROR_COLORS[error.errorType] ||
              ERROR_COLORS[ClassificationType.INCORRECT_EXTRACTION];

            return (
              <div
                key={error.errorType}
                className={cn(colors.bar, "h-full")}
                style={{ width: `${width}%` }}
                title={`${ERROR_LABELS[error.errorType]}: ${error.total}`}
              />
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3">
          {errors.map((error) => {
            const colors =
              ERROR_COLORS[error.errorType] ||
              ERROR_COLORS[ClassificationType.INCORRECT_EXTRACTION];
            const label = ERROR_LABELS[error.errorType] || error.errorType;

            return (
              <div key={error.errorType} className="flex items-center gap-2">
                <div className={cn("h-2 w-2 rounded-full", colors.bar)} />
                <span className="text-xs text-gray-600">
                  {label}: <span className="font-medium">{error.total}</span>
                </span>
              </div>
            );
          })}
        </div>

        {personId && (
          <div className="border-t pt-3">
            <button
              onClick={handleShowDetails}
              className="flex w-full items-center justify-between rounded bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>View Detailed Breakdown</span>
              </div>
              {showDetails ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>

            {showDetails && (
              <div className="mt-2 max-h-96 overflow-y-scroll rounded border bg-white">
                {evalLoading ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    Loading...
                  </div>
                ) : (
                  <>
                    {evaluations.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        No mistakes found
                      </div>
                    ) : (
                      <table className="w-full table-fixed text-xs">
                        <thead className="sticky top-0 z-20 bg-gray-50">
                          <tr>
                            <th className="w-[120px] px-2 py-1.5 text-left font-medium text-gray-500">
                              Document
                            </th>
                            <th className="w-[100px] px-2 py-1.5 text-left font-medium text-gray-500">
                              Field
                            </th>
                            <th className="w-[80px] px-2 py-1.5 text-left font-medium text-gray-500">
                              Submitted
                            </th>
                            <th className="w-8 px-2 py-1.5 text-center font-medium text-gray-500"></th>
                            <th className="w-[80px] px-2 py-1.5 text-left font-medium text-gray-500">
                              Correct
                            </th>
                            <th className="w-[100px] px-2 py-1.5 text-left font-medium text-gray-500">
                              <Popover
                                open={errorTypePopoverOpen}
                                onOpenChange={setErrorTypePopoverOpen}
                              >
                                <PopoverTrigger asChild>
                                  <button
                                    onClick={(e) => e.stopPropagation()}
                                    className={cn(
                                      "flex items-center gap-1 rounded px-1 py-0.5 hover:bg-gray-200",
                                      selectedErrorType !== "ALL" &&
                                        "bg-blue-100 text-blue-700",
                                    )}
                                  >
                                    {selectedErrorType === "ALL"
                                      ? "Error Type"
                                      : ERROR_LABELS[selectedErrorType]}
                                    <FilterIcon className="h-3 w-3" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-32 p-0"
                                  align="start"
                                >
                                  <div
                                    onClick={() => {
                                      setSelectedErrorType("ALL");
                                      setErrorTypePopoverOpen(false);
                                    }}
                                    className={cn(
                                      "cursor-pointer px-2 py-1 text-xs hover:bg-gray-100",
                                      selectedErrorType === "ALL" &&
                                        "bg-gray-100 font-medium",
                                    )}
                                  >
                                    All Types
                                  </div>
                                  {availableErrorTypes.map((type) => (
                                    <div
                                      key={type}
                                      onClick={() => {
                                        setSelectedErrorType(type);
                                        setErrorTypePopoverOpen(false);
                                      }}
                                      className={cn(
                                        "cursor-pointer px-2 py-1 text-xs hover:bg-gray-100",
                                        selectedErrorType === type &&
                                          "bg-gray-100 font-medium",
                                      )}
                                    >
                                      {ERROR_LABELS[type] || type}
                                    </div>
                                  ))}
                                </PopoverContent>
                              </Popover>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {evaluations.map((evaluation) => {
                            const classificationType =
                              evaluation.classification ||
                              ClassificationType.INCORRECT_EXTRACTION;
                            const errorColors =
                              ERROR_COLORS[classificationType] ||
                              ERROR_COLORS[
                                ClassificationType.INCORRECT_EXTRACTION
                              ];
                            const errorLabel =
                              ERROR_LABELS[classificationType] ||
                              classificationType;

                            return (
                              <tr
                                key={evaluation.id}
                                className="cursor-pointer hover:bg-gray-50"
                                onClick={() =>
                                  setSelectedEvaluation(evaluation)
                                }
                              >
                                <td className="max-w-[120px] truncate px-2 py-1.5 text-gray-900">
                                  {evaluation.document.name}
                                </td>
                                <td className="max-w-[100px] truncate px-2 py-1.5 text-gray-600">
                                  {evaluation.field.displayName ||
                                    evaluation.field.name}
                                </td>
                                <td className="max-w-[80px] truncate px-2 py-1.5 text-red-600">
                                  {evaluation.value || "-"}
                                </td>
                                <td className="px-2 py-1.5 text-center text-gray-400">
                                  <ArrowRight className="inline h-3 w-3" />
                                </td>
                                <td className="max-w-[80px] truncate px-2 py-1.5 text-green-600">
                                  {evaluation.finalValue || "-"}
                                </td>
                                <td className="px-2 py-1.5">
                                  <span
                                    className={cn(
                                      "whitespace-nowrap rounded px-1.5 py-0.5 text-xs",
                                      errorColors.bg,
                                      errorColors.text,
                                    )}
                                  >
                                    {errorLabel}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
